import { Grid } from '@chakra-ui/react';
import NextLink from 'next/link';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { BackToTop } from '@/components/ui/core/BackToTop/BackToTop';
import { PublisherCard } from '@/components/ui/features/hubs-publishers/PublisherCard/PublisherCard';
import { LectureCardSkeleton } from '@/components/ui/shared/lecture/LectureCard/LectureCardSkeleton/LectureCardSkeleton';
import { NoResults } from '@/components/ui/shared/messages/NoResults/NoResults';
import useElement from '@/hooks/useElement';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { ListResponse } from '@/lib/datx-jsonapi-react/interfaces/ListResponse';
import { ThinConference } from '@/resources/ThinConference';

interface ILectureURLSearchParams {
  query?: string;
}

interface IPublisherGridProps {
  hubId: string;
  items: Array<ThinConference>;
  isLoadingMore: boolean;
  isReachingEnd: boolean;
  isEmpty: boolean;
  onLoadMore: () => Promise<Array<ListResponse<ThinConference>>>;
  error: Error;
  pageSize: number;
}

export const HubPublishersGrid: FC<IPublisherGridProps> = ({
  hubId,
  items,
  isLoadingMore,
  isEmpty,
  isReachingEnd,
  onLoadMore,
  error,
  pageSize,
}) => {
  const { element: lectureListElement, ref: lectureListRef } = useElement();
  const { t } = useTranslation();
  const { query } = useQueryParams<ILectureURLSearchParams>({
    query: StringParam,
  });

  if (isEmpty) {
    return <NoResults title={query ? t('common.noResultsForQuery', { query }) : t('common.noResults')} />;
  }

  return (
    <Fragment>
      <Grid
        ref={lectureListRef}
        templateColumns={{
          base: 'repeat(auto-fill, minmax(min(100%, 336px), 1fr))',
          lg: 'repeat(auto-fill, minmax(min(100%, 376px), 1fr))',
        }}
        gap={4}
        h="max-content"
      >
        {items.map((publisher) => (
          <NextLink key={publisher.id} href={`/publisher/${publisher.slug}`} passHref>
            <PublisherCard hubId={hubId} publisher={publisher} />
          </NextLink>
        ))}

        {isLoadingMore ? Array.from({ length: pageSize }, (_, i) => <LectureCardSkeleton key={i} />) : null}
      </Grid>

      <InView
        as="span"
        rootMargin="200px"
        onChange={(inView) => {
          if (inView && !error && !isReachingEnd) {
            onLoadMore?.();
          }
        }}
      />

      {!isEmpty && <BackToTop parentTopOffset={lectureListElement?.offsetTop} scrollingOffset={280} />}
    </Fragment>
  );
};
